import {
  DatabaseIcon,
  LockClosedIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

// Same certification badge assets the footer uses — claims must stay in sync
// with the footer and the Trust Center.
const CERTIFICATIONS = [
  { name: 'SOC 2', label: 'Certified', image: '/images/soc2.png' },
  { name: 'ISO 27001', label: 'Certified', image: '/images/iso-27001.png' },
  { name: 'HIPAA', label: 'Compliant', image: '/images/hipaa.png' },
  { name: 'GDPR', label: 'Compliant', image: '/images/gdpr.png' },
];

const ASSURANCES: Array<{
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}> = [
  {
    Icon: LockClosedIcon,
    title: 'We never train on your data',
    description:
      'Your prompts, files and creations are never used to train AI models.',
  },
  {
    Icon: DatabaseIcon,
    title: 'You own everything you make',
    description: 'Full commercial rights to every asset, on every paid plan.',
  },
  {
    Icon: UserGroupIcon,
    title: 'Private by default',
    description: 'Your work is yours alone unless you choose to share it.',
  },
  {
    Icon: TrashIcon,
    title: 'Delete anytime',
    description:
      'Encrypted in transit and at rest — and gone for good when you say so.',
  },
];

const TrustSection = () => {
  const { t } = useLocale('home');

  return (
    <section className='relative border-y border-white/[0.06] bg-white/[0.02] py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
          {/* Ownership + assurances */}
          <div>
            <div className='text-sm font-bold uppercase tracking-widest text-indigo-300'>
              {t('Privacy & security')}
            </div>
            <h2 className='mt-3 text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
              {t('Your work stays yours.')}
            </h2>
            <p className='mt-4 max-w-xl text-lg leading-relaxed text-slate-400'>
              {t(
                'Every asset you create belongs to you. We never train on your data — and enterprise-grade security keeps it that way.',
              )}
            </p>

            <div className='mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2'>
              {ASSURANCES.map((assurance) => (
                <div key={assurance.title} className='flex items-start gap-3'>
                  <span className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 ring-1 ring-inset ring-emerald-400/20'>
                    <assurance.Icon className='h-5 w-5' aria-hidden='true' />
                  </span>
                  <span>
                    <span className='block font-bold text-white'>
                      {t(assurance.title)}
                    </span>
                    <span className='mt-0.5 block text-sm leading-snug text-slate-400'>
                      {t(assurance.description)}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <a
              href='https://easypeasyai.trust.site/'
              target='_blank'
              rel='noreferrer'
              className='mt-8 inline-flex items-center gap-2 font-bold text-indigo-300 transition-colors hover:text-indigo-200'
            >
              {t('Visit our Trust Center')} <span aria-hidden='true'>→</span>
            </a>
          </div>

          {/* Certification tiles */}
          <div className='grid grid-cols-2 gap-4'>
            {CERTIFICATIONS.map((certification) => (
              <div
                key={certification.name}
                className='flex flex-col items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-8 text-center'
              >
                <Image
                  src={certification.image}
                  width={64}
                  height={64}
                  alt={`${certification.name} ${certification.label}`}
                  title={`${certification.name} ${certification.label}`}
                />
                <div>
                  <div className='font-extrabold text-white'>
                    {certification.name}
                  </div>
                  <div className='text-sm text-slate-400'>
                    {t(certification.label)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
